import React,{useState,useEffect} from 'react'
import {Link} from 'react-router-dom'
import {motion} from 'framer-motion'
import SEO from '../components/SEO'
import {useAuth} from '../contexts/AuthContext'
import { FiMail, FiArrowLeft, FiLoader, FiSend } from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import Logo from '../components/common/Logo'

function ForgotPassword() {
  const {resetPassword}=useAuth()
  const [email,setEmail]=useState('')
  const [loading,setLoading]=useState(false)
  const [error,setError]=useState('')
  const [success,setSuccess]=useState('')

  // Add auth-page class for iOS safe area styling
  useEffect(() => {
    document.documentElement.classList.add('auth-page');
    document.body.classList.add('auth-page');
    return () => {
      document.documentElement.classList.remove('auth-page');
      document.body.classList.remove('auth-page');
    };
  }, []);

  const handleSubmit=async (e)=> {
    e.preventDefault()

    if (!email) {
      setError('Please enter your email address')
      return
    }

    const emailRegex=/^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    setError('')
    setSuccess('')

    const {error}=await resetPassword(email)

    if (error) {
      setError(error.message)
    } else {
      setSuccess('Password reset email sent! Check your inbox for instructions.')
    }

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-app flex items-center justify-center safe-area-inset-top">
      <SEO title="Reset Password | TeeSync" noindex={true} />
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{duration: 0.5}}
        className="max-w-md w-full mx-4"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            whileHover={{scale: 1.05}}
            transition={{type: "spring",stiffness: 300}}
            className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center shadow-card mx-auto mb-4"
          >
            <Logo size="lg" variant="white" />
          </motion.div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Reset Password</h1>
          <p className="text-text-secondary">Enter your email to receive reset instructions</p>
        </div>

        {/* Reset Form */}
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.2}}
          className="bg-surface rounded-xl border border-border-subtle shadow-card p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                className="bg-scarlet-50 border border-scarlet-200 text-scarlet-700 px-4 py-3 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{opacity: 0,x: -20}}
                animate={{opacity: 1,x: 0}}
                className="bg-accent/10 border border-accent text-accent px-4 py-3 rounded-lg text-sm"
              >
                {success}
              </motion.div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-secondary mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted w-5 h-5" />
                <input
                  type="email"
                  id="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e)=> setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-border-subtle rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent bg-app text-text-primary placeholder-text-muted"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? (
                <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Send Reset Email
                  <SafeIcon icon={FiSend} className="w-5 h-5 ml-2" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-accent hover:text-accent font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
              Back to Sign In
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  )
}

export default ForgotPassword
